/* HR — Payroll Breakdown（個人薪資拆解視圖，唯讀）
 *
 *  給 finance / hr / admin 向員工逐項解釋某月薪資金額由來。
 *  選員工 + 年/月 → 找出該月薪資單 → 攤開分組明細，每行附計算過程公式。
 *
 *  Endpoints:
 *    GET /api/employees?page_size=200                              → 員工選單
 *    GET /api/payroll/payslips?employee_id=&year=&month=&page_size=1  → 取得該月薪資單 id
 *    GET /api/payroll/payslips/:id/breakdown                       → 拆解（不重算，讀引擎快照）
 *
 *  純唯讀：無編輯、無匯出。掛在「薪資」頁的分頁下（PayrollPage tab）。
 */

function PayrollBreakdownTab() {
  const toast = useToast();
  const now = new Date();
  const [employees, setEmployees] = React.useState([]);
  const [empId, setEmpId] = React.useState('');
  const [year, setYear] = React.useState(now.getFullYear());
  const [month, setMonth] = React.useState(now.getMonth() + 1);
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);   // { status, employee, period, groups, reconcile }

  // 載入員工選單（一次）
  React.useEffect(() => {
    api('GET', '/employees?page_size=200')
      .then(resp => setEmployees(resp?.items || []))
      .catch(() => setEmployees([]));
  }, []);

  const query = React.useCallback(async () => {
    if (!empId) { toast('請先選擇員工', 'error'); return; }
    setLoading(true);
    setData(null);
    try {
      const list = await api(
        'GET',
        `/payroll/payslips?employee_id=${empId}&year=${year}&month=${month}&page_size=1`,
      );
      const slip = (list?.items || [])[0];
      if (!slip) {
        setData({ status: 'no_slip' });
        return;
      }
      const br = await api('GET', `/payroll/payslips/${slip.id}/breakdown`);
      setData(br);
    } catch (e) {
      toast('查詢失敗：' + (e.detail || ''), 'error');
    } finally {
      setLoading(false);
    }
  }, [empId, year, month, toast]);

  const empName = data?.employee?.name
    || employees.find(e => String(e.id) === String(empId))?.name
    || '';

  return (
    <>
      {/* === 查詢列 === */}
      <Card padding="0" eyebrow="Payroll Breakdown" title="薪資拆解" italic="逐項計算過程說明">
        <div style={{
          display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end',
          padding: '16px 24px',
        }}>
          <Field label="員工">
            <select value={empId} onChange={(e) => setEmpId(e.target.value)}
              style={inputStyle({ minWidth: 200 })}>
              <option value="">— 選擇員工 —</option>
              {employees.map(e => (
                <option key={e.id} value={e.id}>
                  {e.emp_code ? `${e.emp_code} · ` : ''}{e.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="年">
            <select value={year} onChange={(e) => setYear(parseInt(e.target.value))}
              style={inputStyle({ width: 110, fontFamily: HR.mono })}>
              {[year - 2, year - 1, year, year + 1].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </Field>
          <Field label="月">
            <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))}
              style={inputStyle({ width: 90, fontFamily: HR.mono })}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Button variant="primary" onClick={query} disabled={loading}>
            {loading ? '查詢中…' : '查詢拆解'}
          </Button>
        </div>
      </Card>

      {/* === 結果區 === */}
      {loading && <div style={{ padding: 40, display: 'grid', placeItems: 'center' }}><Spinner size={20} /></div>}

      {!loading && data?.status === 'no_slip' && (
        <div style={{ marginTop: 16 }}>
          <EmptyState icon="file-text" title="此月查無薪資單"
            hint={`${year} 年 ${month} 月尚未為此員工建立薪資單。`} />
        </div>
      )}

      {!loading && data?.status === 'not_calculated' && (
        <div style={{ marginTop: 16 }}>
          <EmptyState icon="alert-circle" title="此月尚未計算，無法拆解"
            hint="薪資單存在但尚未執行「計算」，無拆解快照可顯示。" />
        </div>
      )}

      {!loading && data?.status === 'ok' && (
        <div style={{ marginTop: 16 }}>
          <div style={uiStyle({
            marginBottom: 14, fontSize: FontSizes.xs, color: HR.inkSoft, fontFamily: HR.serif,
          }, { fontSize: FontSizes.sm, fontWeight: 600 })}>
            <strong style={uiStyle({ color: HR.ink }, { fontWeight: 800 })}>{empName}</strong> · {data.period.year} 年 {data.period.month} 月薪資拆解
          </div>

          {data.reconcile && data.reconcile.ok === false && (
            <div style={uiStyle({
              marginBottom: 14, padding: 12, borderRadius: 4,
              background: '#FBEFEF', border: `1px solid ${HR.down}`,
              color: HR.down, fontSize: FontSizes.xxs,
            }, {
              padding: '12px 16px', borderRadius: GxT.radius.inner, border: 'none',
              background: GxT.tint.down.bg, color: GxT.tint.down.fg, fontWeight: 700,
            })}>
              ⚠ 對帳不符：明細推算實發 {fmtMoney(data.reconcile.computed_net)}，
              薪資單記錄 {fmtMoney(data.reconcile.expected_net)}。請通知 HR 重新計算後再向員工說明。
            </div>
          )}

          <div className="hr-payslip-panels" style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16,
          }}>
            <ExplainGroup title="應發明細" accent={HR.accent} rows={data.groups.earnings} />
            <ExplainGroup title="應扣明細" accent={HR.down} rows={data.groups.deductions} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <ExplainGroup title="請假與補班時數" accent={HR.inkSoft} rows={data.groups.hours} />
          </div>
          <ExplainGroup title="合計" accent={HR.ink} rows={data.groups.totals} />
          {/* 資遣月才有（payroll_explain 新分組 severance：資遣費 + 本期實付合計）；舊快照無此分組 → 不顯示 */}
          {Array.isArray(data.groups.severance) && data.groups.severance.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <ExplainGroup title="資遣費（非薪資所得）" accent={HR.warn} rows={data.groups.severance} />
            </div>
          )}
        </div>
      )}
    </>
  );
}

/* 一組可解釋拆解：每行主顯「項目 / 金額」，下方小字灰色顯示計算過程公式。 */
function ExplainGroup({ title, accent, rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div style={uiStyle({
      padding: 14, background: HR.surface,
      border: `1px solid ${HR.rule}`, borderRadius: 5,
    }, {
      // Expressive (GX): borderless card on the page background (plan §2(d)-6) — visual only
      padding: '18px 20px', border: 'none', borderRadius: GxT.radius.card,
    })}>
      <div style={uiStyle({
        fontSize: FontSizes.trading_md, color: HR.inkFaint, letterSpacing: '0.1em',
        textTransform: 'uppercase', marginBottom: 10, paddingBottom: 6,
        borderBottom: `2px solid ${accent}`,
      }, {
        fontSize: GxT.size.h3, fontWeight: GxT.weight.title, letterSpacing: 0, textTransform: 'none',
        color: GxT.color.on, paddingBottom: 8,
      })}>{title}</div>
      <table style={{ width: '100%', fontSize: FontSizes.xs, borderCollapse: 'collapse' }}>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTop: i === 0 ? 'none' : `1px solid ${HR.ruleSoft}` }}>
              <td style={{ padding: '8px 0', verticalAlign: 'top' }}>
                <div style={{ color: HR.ink, fontWeight: uiPick(500, 600) }}>{r.label}</div>
                {r.formula && (
                  <div style={{
                    marginTop: 2, fontSize: FontSizes.trading_md, color: HR.inkFaint,
                    fontFamily: HR.mono,
                  }}>{r.formula}</div>
                )}
              </td>
              <td style={{
                padding: '8px 0', textAlign: 'right', verticalAlign: 'top',
                fontFamily: HR.mono, fontWeight: 600,
                color: (r.text == null && r.amount < 0) ? HR.down : HR.ink,
                whiteSpace: 'nowrap',
              }}>
                {r.text != null ? r.text : fmtMoney(r.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

window.PayrollBreakdownTab = PayrollBreakdownTab;
